import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ─── NPS Slider Component ───
function NPSSlider({ value, onChange, labels }) {
  const colors = ['#E53935','#E53935','#EF5350','#FF7043','#FFA726','#FFCA28','#FFEE58','#C6FF00','#76FF03','#00E676','#00C853'];
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#888', marginBottom:6 }}>
        <span>{labels?.min || '0'}</span><span>{labels?.max || '10'}</span>
      </div>
      <div style={{ display:'flex', gap:4 }}>
        {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            style={{
              flex:1, padding:'12px 0', borderRadius:10, border: value === n ? '2px solid #333' : '2px solid transparent',
              background: value === n ? colors[n] : '#f0f0f0', color: value === n ? '#fff' : '#666',
              fontWeight: value === n ? 800 : 600, fontSize:15, cursor:'pointer',
              transition:'all .2s', transform: value === n ? 'scale(1.1)' : 'scale(1)',
              boxShadow: value === n ? '0 4px 12px rgba(0,0,0,.2)' : 'none',
            }}>
            {n}
          </button>
        ))}
      </div>
      {value !== null && value !== undefined && (
        <div style={{ textAlign:'center', marginTop:8, fontSize:13, color:'#666' }}>
          {value <= 6 ? '😟' : value <= 8 ? '😊' : '🤩'} Seleccionaste <strong>{value}</strong>
        </div>
      )}
    </div>
  );
}

// ─── Star Rating Component ───
function StarRating({ value, onChange, max = 5 }) {
  const [hover, setHover] = useState(null);
  return (
    <div style={{ display:'flex', gap:4 }}>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => (
        <button key={n} type="button" onClick={() => onChange(n)}
          onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(null)}
          style={{
            background:'transparent', border:'none', fontSize:28, cursor:'pointer',
            transition:'transform .15s', transform: (hover || value) >= n ? 'scale(1.2)' : 'scale(1)',
            filter: (hover || value) >= n ? 'none' : 'grayscale(1) opacity(0.3)',
          }}>
          ⭐
        </button>
      ))}
    </div>
  );
}

// ─── Likert Slider Row ───
function LikertRow({ label, value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 0', borderBottom:'1px solid #f0f0f0' }}>
      <span style={{ flex:'1 1 140px', fontSize:13, color:'#444' }}>{label}</span>
      <div style={{ display:'flex', gap:3, flex:'0 0 auto' }}>
        {[1,2,3,4,5,6,7,8,9,10].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            style={{
              width:28, height:28, borderRadius:'50%', border:'none', cursor:'pointer',
              background: value >= n ? (n <= 3 ? '#ef5350' : n <= 6 ? '#ffa726' : '#66bb6a') : '#e8e8e8',
              color: value >= n ? '#fff' : '#aaa', fontSize:11, fontWeight:700,
              transition:'all .15s',
            }}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Multi-Select Chips ───
function MultiSelect({ options, selected, onChange, max }) {
  const toggle = (val) => {
    if (selected.includes(val)) {
      onChange(selected.filter(v => v !== val));
    } else if (selected.length < max) {
      onChange([...selected, val]);
    }
  };
  return (
    <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => toggle(o.value)}
          style={{
            padding:'8px 16px', borderRadius:20, cursor:'pointer', fontSize:13,
            border: selected.includes(o.value) ? '2px solid #7c3aed' : '2px solid #e0e0e0',
            background: selected.includes(o.value) ? '#ede9fe' : '#fff',
            color: selected.includes(o.value) ? '#7c3aed' : '#555',
            fontWeight: selected.includes(o.value) ? 600 : 400,
            transition:'all .2s',
          }}>
          {o.label}
        </button>
      ))}
      <div style={{ width:'100%', fontSize:11, color:'#999', marginTop:2 }}>
        {selected.length}/{max} seleccionados
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export default function SatisfactionSurveyPage() {
  const { tipo } = useParams();
  const surveyType = tipo === 'heladeria' ? 'heladeria' : 'topping_frozen';

  const [config, setConfig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0 = welcome, 1..n = questions, n+1 = contact, n+2 = thanks
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contact, setContact] = useState({ name: '', phone: '', email: '' });

  useEffect(() => {
    fetch(`${API_BASE}/public/survey/config/${surveyType}`)
      .then(r => r.json())
      .then(d => { if (d.success) setConfig(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [surveyType]);

  const setAnswer = useCallback((qId, val) => {
    setAnswers(prev => ({ ...prev, [qId]: val }));
  }, []);

  const questions = config?.questions || [];
  const totalSteps = questions.length + 2; // welcome + questions + contact/submit
  const progress = step > 0 ? Math.min((step / totalSteps) * 100, 100) : 0;
  const brandColor = config?.brandColor || '#7c3aed';

  const currentQ = step >= 1 && step <= questions.length ? questions[step - 1] : null;

  const canAdvance = () => {
    if (!currentQ) return true;
    if (!currentQ.required) return true;
    const val = answers[currentQ.id];
    if (currentQ.type === 'nps') return val !== undefined && val !== null;
    if (currentQ.type === 'radio') return !!val;
    if (currentQ.type === 'multiselect') return Array.isArray(val) && val.length > 0;
    if (currentQ.type === 'likert_multi' || currentQ.type === 'stars_multi') {
      return currentQ.aspects?.every(a => answers[currentQ.id]?.[a.id] !== undefined);
    }
    if (currentQ.type === 'textarea') return !!val?.trim();
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/public/survey/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey_type: surveyType,
          responses: answers,
          customer_name: contact.name || null,
          customer_phone: contact.phone || null,
          customer_email: contact.email || null,
        }),
      });
      const data = await res.json();
      if (data.success) setSubmitted(true);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ color:'#fff', fontSize:18 }}>Cargando encuesta...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#f5f5f5' }}>
        <div style={{ textAlign:'center', padding:40 }}>
          <div style={{ fontSize:60, marginBottom:16 }}>❌</div>
          <h2 style={{ fontSize:20, color:'#333' }}>Encuesta no encontrada</h2>
        </div>
      </div>
    );
  }

  // Thank you screen
  if (submitted) {
    return (
      <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:`linear-gradient(135deg, ${brandColor} 0%, ${brandColor}CC 100%)`, padding:20 }}>
        <div style={{ background:'#fff', borderRadius:24, padding:48, maxWidth:440, width:'100%', textAlign:'center', boxShadow:'0 20px 60px rgba(0,0,0,.2)' }}>
          <div style={{ fontSize:80, marginBottom:16, animation:'bounce 1s ease infinite' }}>🎉</div>
          <h2 style={{ fontSize:24, fontWeight:800, color:'#333', marginBottom:8 }}>¡Muchas gracias!</h2>
          <p style={{ fontSize:15, color:'#666', lineHeight:1.6 }}>
            Tu opinión es muy valiosa para nosotros. Nos ayuda a seguir mejorando cada día.
          </p>
          <div style={{ marginTop:24, padding:16, background:'#f0fdf4', borderRadius:12, border:'1px solid #bbf7d0' }}>
            <p style={{ fontSize:13, color:'#166534' }}>✅ Respuesta registrada exitosamente</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render question by type ──
  const renderQuestion = (q) => {
    switch (q.type) {
      case 'nps':
        return <NPSSlider value={answers[q.id]} onChange={v => setAnswer(q.id, v)} labels={q.labels} />;

      case 'radio':
        return (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {q.options.map(o => (
              <button key={o.value} type="button" onClick={() => setAnswer(q.id, o.value)}
                style={{
                  padding:'12px 16px', borderRadius:12, textAlign:'left', cursor:'pointer', fontSize:14,
                  border: answers[q.id] === o.value ? `2px solid ${brandColor}` : '2px solid #e5e7eb',
                  background: answers[q.id] === o.value ? `${brandColor}15` : '#fff',
                  color: answers[q.id] === o.value ? brandColor : '#444',
                  fontWeight: answers[q.id] === o.value ? 600 : 400,
                  transition:'all .2s',
                }}>
                {o.label}
              </button>
            ))}
          </div>
        );

      case 'multiselect':
        return (
          <MultiSelect options={q.options} max={q.maxSelections || 3}
            selected={answers[q.id] || []}
            onChange={v => setAnswer(q.id, v)} />
        );

      case 'likert_multi':
        return (
          <div>
            {q.aspects.map(a => (
              <LikertRow key={a.id} label={a.label}
                value={answers[q.id]?.[a.id]}
                onChange={v => setAnswer(q.id, { ...(answers[q.id] || {}), [a.id]: v })} />
            ))}
          </div>
        );

      case 'stars_multi':
        return (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {q.aspects.map(a => (
              <div key={a.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'6px 0', borderBottom:'1px solid #f0f0f0' }}>
                <span style={{ flex:'1 1 140px', fontSize:13, color:'#444' }}>{a.label}</span>
                <StarRating value={answers[q.id]?.[a.id]}
                  onChange={v => setAnswer(q.id, { ...(answers[q.id] || {}), [a.id]: v })} />
              </div>
            ))}
          </div>
        );

      case 'textarea':
        return (
          <textarea value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)}
            placeholder={q.placeholder || ''}
            rows={4}
            style={{
              width:'100%', padding:12, borderRadius:12, border:'2px solid #e5e7eb', fontSize:14,
              resize:'vertical', fontFamily:'inherit', outline:'none',
              boxSizing:'border-box',
            }}
            onFocus={e => e.target.style.borderColor = brandColor}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#f8fafc', fontFamily:"'Inter','Segoe UI',sans-serif" }}>
      {/* Progress bar */}
      <div style={{ position:'fixed', top:0, left:0, right:0, height:4, background:'#e5e7eb', zIndex:50 }}>
        <div style={{ height:'100%', background:`linear-gradient(90deg, ${brandColor}, ${brandColor}CC)`, width:`${progress}%`, transition:'width .4s ease', borderRadius:'0 4px 4px 0' }} />
      </div>

      <div style={{ maxWidth:520, margin:'0 auto', padding:'60px 20px 40px' }}>

        {/* Welcome Screen */}
        {step === 0 && (
          <div style={{ textAlign:'center', animation:'fadeIn .5s ease' }}>
            <div style={{ fontSize:60, marginBottom:16 }}>
              {surveyType === 'heladeria' ? '🍦' : '🧊'}
            </div>
            <h1 style={{ fontSize:24, fontWeight:800, color:'#1e293b', marginBottom:8, lineHeight:1.3 }}>
              {config.title}
            </h1>
            <p style={{ fontSize:15, color:'#64748b', lineHeight:1.6, marginBottom:32 }}>
              {config.subtitle}
            </p>
            <p style={{ fontSize:13, color:'#94a3b8', marginBottom:24 }}>
              ⏱️ Toma aproximadamente {surveyType === 'heladeria' ? '2' : '3'} minutos
            </p>
            <button onClick={() => setStep(1)}
              style={{
                padding:'14px 40px', borderRadius:14, border:'none', cursor:'pointer',
                background:`linear-gradient(135deg, ${brandColor}, ${brandColor}DD)`,
                color:'#fff', fontSize:16, fontWeight:700, boxShadow:`0 8px 24px ${brandColor}44`,
                transition:'transform .2s',
              }}
              onMouseDown={e => e.target.style.transform = 'scale(0.96)'}
              onMouseUp={e => e.target.style.transform = 'scale(1)'}>
              Comenzar encuesta →
            </button>
          </div>
        )}

        {/* Question Steps */}
        {currentQ && (
          <div style={{ animation:'fadeIn .3s ease' }}>
            <div style={{ fontSize:12, color:'#94a3b8', marginBottom:8, fontWeight:600 }}>
              PREGUNTA {step} DE {questions.length}
            </div>
            <h2 style={{ fontSize:18, fontWeight:700, color:'#1e293b', marginBottom:20, lineHeight:1.4 }}>
              {currentQ.text}
            </h2>

            {renderQuestion(currentQ)}

            <div style={{ display:'flex', justifyContent:'space-between', marginTop:28, gap:12 }}>
              <button onClick={() => setStep(s => Math.max(0, s - 1))}
                style={{
                  padding:'12px 24px', borderRadius:12, border:'2px solid #e5e7eb', background:'#fff',
                  color:'#64748b', fontSize:14, fontWeight:600, cursor:'pointer',
                }}>
                ← Atrás
              </button>
              <button onClick={() => setStep(s => s + 1)} disabled={!canAdvance()}
                style={{
                  padding:'12px 24px', borderRadius:12, border:'none', cursor: canAdvance() ? 'pointer' : 'not-allowed',
                  background: canAdvance() ? `linear-gradient(135deg, ${brandColor}, ${brandColor}DD)` : '#e5e7eb',
                  color: canAdvance() ? '#fff' : '#aaa', fontSize:14, fontWeight:600,
                  transition:'all .2s',
                }}>
                {step === questions.length ? 'Finalizar →' : 'Siguiente →'}
              </button>
            </div>
          </div>
        )}

        {/* Contact + Submit (optional) */}
        {step === questions.length + 1 && (
          <div style={{ animation:'fadeIn .3s ease' }}>
            <div style={{ textAlign:'center', marginBottom:24 }}>
              <div style={{ fontSize:48, marginBottom:8 }}>📝</div>
              <h2 style={{ fontSize:20, fontWeight:700, color:'#1e293b', marginBottom:8 }}>¡Casi terminamos!</h2>
              <p style={{ fontSize:14, color:'#64748b' }}>Si deseas, déjanos tus datos para darte una mejor atención. (Opcional)</p>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:24 }}>
              <input type="text" placeholder="Tu nombre (opcional)" value={contact.name}
                onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                style={{ padding:12, borderRadius:12, border:'2px solid #e5e7eb', fontSize:14, outline:'none', fontFamily:'inherit' }} />
              <input type="tel" placeholder="Tu teléfono (opcional)" value={contact.phone}
                onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                style={{ padding:12, borderRadius:12, border:'2px solid #e5e7eb', fontSize:14, outline:'none', fontFamily:'inherit' }} />
              <input type="email" placeholder="Tu email (opcional)" value={contact.email}
                onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                style={{ padding:12, borderRadius:12, border:'2px solid #e5e7eb', fontSize:14, outline:'none', fontFamily:'inherit' }} />
            </div>

            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setStep(s => s - 1)}
                style={{ flex:1, padding:'14px', borderRadius:12, border:'2px solid #e5e7eb', background:'#fff', color:'#64748b', fontSize:14, fontWeight:600, cursor:'pointer' }}>
                ← Atrás
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                style={{
                  flex:2, padding:'14px', borderRadius:12, border:'none', cursor:'pointer',
                  background:`linear-gradient(135deg, ${brandColor}, ${brandColor}DD)`,
                  color:'#fff', fontSize:16, fontWeight:700, boxShadow:`0 8px 24px ${brandColor}44`,
                  opacity: submitting ? 0.6 : 1,
                }}>
                {submitting ? 'Enviando...' : '🚀 Enviar encuesta'}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
